/** Подписи и чистая логика «Поставок» и «Проверок» (не компоненты): методы оценки склада,
 *  статусы хранения, флаги, путь поставки цепочкой, статусы проверок, полоса истории. Портал
 *  ничего не пересчитывает: только раскладка полей бэкенда. Значение вне контракта не прячется —
 *  показывается своим кодом с пометкой. */
import {
  PLANNED,
  type CheckStatus,
  type StorageStatus,
  type SupplyStatusFilter,
  type WbCheck,
  type WbCheckHistoryRow,
  type WbSupply,
  type WbSupplyPlace,
} from "@/api/wbRegions";

/** Метод оценки склада поставки (контракт, порядок — от надёжного к слабому). */
export const METHOD_LABELS: Record<string, { short: string; long: string }> = {
  chain: { short: "цепочка", long: "цепочка хранения: ≥ 90 % штук на складе" },
  dlv_map: { short: "логистика", long: "продажи по коэффициенту логистики (карта dlv_prc)" },
  buyer_profile: {
    short: "покупатели",
    long: "регионы покупателей; склад внутри региона — склад эталона, не факт",
  },
  chain_majority: { short: "цепочка (большинство)", long: "большинство штук цепочки хранения" },
  attr19: { short: "attr19", long: "склад назначения из кабинета (attr19) — подсказка, не факт" },
  accept_city: { short: "город приёмки", long: "склад города приёмки" },
  unknown: { short: "не определён", long: "склад поставки не определён" },
};

export function methodLabel(m: string | null): { short: string; long: string; known: boolean } {
  if (m === null) return { short: "нет метода", long: "метод в витрине пуст", known: false };
  const l = METHOD_LABELS[m];
  return l ? { ...l, known: true } : { short: m, long: `метод «${m}» вне контракта`, known: false };
}

export const STORAGE_LABELS: Record<StorageStatus, string> = {
  in_storage: "в хранении",
  // не «запланирована»: сюда попадают и принятые, не дошедшие до хранения (контракт v1.3)
  no_storage: "без хранения",
  gone: "ушла из хранения",
  frozen_dropped: "на замороженном складе",
};

export function storageLabel(s: string | null): string {
  if (s === null) return "нет статуса";
  return STORAGE_LABELS[s as StorageStatus] ?? `${s} (вне контракта)`;
}

/** «Запланирована» — ещё не принята: `status_name` «Запланировано» / «Отгрузка разрешена». */
export const PLANNED_LABEL = "запланирована";

/** Подпись значения фильтра «Статус» (статус хранения или «запланирована»). */
export function statusFilterLabel(s: SupplyStatusFilter | string): string {
  return s === PLANNED ? PLANNED_LABEL : storageLabel(s);
}

/** Тип пункта приёмки (`accept_kind`, контракт v1.3): подпись в чипе и пояснение. Значение вне
 *  списка — как есть; NULL — атрибутов supplies у поставки нет. */
export function acceptKindLabel(k: string | null): { label: string | null; title: string } {
  if (k === "warehouse") return { label: "склад WB", title: "склад WB: принимает и хранит сам" };
  if (k === "sc") {
    return {
      label: "СЦ",
      title: "сортировочный центр: принял и отправил дальше на склад (куда — attr19 подсказкой, оценка — склад оценки)",
    };
  }
  if (k === null) {
    return { label: null, title: "пункта приёмки нет: у поставки нет атрибутов supplies — известна только по gi_id" };
  }
  return { label: k, title: `тип пункта приёмки «${k}»` };
}

/** Отрыв профиля покупателей (`profile_margin`, 0–2): метод «покупатели» берётся при ≥ 0,3. */
export const PROFILE_MARGIN_MIN = 0.3;

export function profileMarginTitle(m: number | null): string {
  const base =
    "отрыв профиля покупателей (0–2): 0 — профиль одинаково похож на два региона, ~1 — регион однозначен; " +
    `метод «покупатели» берётся при отрыве ≥ ${PROFILE_MARGIN_MIN.toLocaleString("ru-RU")}`;
  if (m === null) return `${base}; профиля нет`;
  return `${base}; здесь ${m.toLocaleString("ru-RU", { maximumFractionDigits: 3 })}${m < PROFILE_MARGIN_MIN ? " — профиль только справочно" : ""}`;
}

/** Флаги поставки (бэкенд `flags`). */
export const FLAG_LABELS: Record<string, string> = {
  attr19_mismatch: "attr19 ≠ данным",
  recon_flag: "расхождение > 10 %",
  recon_not_computed: "без принятого",
};

export function flagLabel(f: string): string {
  return FLAG_LABELS[f] ?? f;
}

/** Склад и регион точки пути: «склад, регион»; нет обоих — null. */
export function placeText(warehouse: string | null, region: string | null): string | null {
  const parts = [warehouse, region].filter((x): x is string => Boolean(x));
  return parts.length ? parts.join(", ") : null;
}

/** Место штук сейчас: склад и регион NULL со штуками — «склад не определён» (настоящее место). */
export function placeName(p: WbSupplyPlace): string {
  if (p.warehouse === null && p.region_wb === null) return "склад не определён";
  return [p.warehouse ?? "склад не определён", p.region_wb].filter(Boolean).join(", ");
}

export type ChipTone = "plain" | "hint" | "danger";

export interface PathChip {
  key: "accept" | "attr19" | "est";
  label: string;
  /** null — точка пути пуста («нет данных») */
  text: string | null;
  tone: ChipTone;
  /** при attr19_mismatch — «по данным — {регион оценки}» */
  note: string | null;
  title: string;
}

/** Путь поставки: Приёмка (СЦ/склад, регион) → attr19 (подсказка кабинета; нет attr19 — точки
 *  нет) → Оценка (склад, регион). */
export function supplyPath(s: WbSupply): PathChip[] {
  const kind = acceptKindLabel(s.accept_kind);
  const accept = placeText(s.accept_warehouse ?? s.accept_point_raw, s.accept_region_wb);
  const chips: PathChip[] = [
    {
      key: "accept",
      label: kind.label ? `Приёмка (${kind.label})` : "Приёмка",
      text: accept,
      tone: "plain",
      note: s.accept_kind === null ? "нет атрибутов supplies" : null,
      title: [
        kind.title,
        s.accept_point_raw && `пункт приёмки: ${s.accept_point_raw}`,
        s.attr18_point_type && `тип пункта (кабинет): ${s.attr18_point_type}`,
        s.transit_warehouse && `транзит: ${s.transit_warehouse}`,
      ]
        .filter(Boolean)
        .join("\n"),
    },
  ];
  if (s.attr19_warehouse !== null || s.attr19_region_wb !== null) {
    const mismatch = s.attr19_mismatch === true;
    chips.push({
      key: "attr19",
      label: "attr19 — подсказка кабинета",
      text: placeText(s.attr19_warehouse, s.attr19_region_wb),
      tone: mismatch ? "danger" : "hint",
      note: mismatch ? `по данным — ${s.region_wb_est ?? "регион не определён"}` : null,
      title:
        "Склад назначения из кабинета (attr19) — подсказка, не склад поставки" +
        (s.lk_new_flow ? "; новый поток кабинета (СЦ → склад назначения)" : ""),
    });
  }
  chips.push({
    key: "est",
    label: "Оценка",
    text: placeText(s.warehouse_est, s.region_wb_est),
    tone: "plain",
    note: null,
    title: s.confidence_reason ?? "",
  });
  return chips;
}

// ─────────────── проверки ───────────────

export const CHECK_STATUS_ORDER: Record<CheckStatus, number> = { fail: 0, warn: 1, ok: 2 };

export const CHECK_STATUS_STYLE: Record<CheckStatus, { label: string; cls: string; cell: string }> = {
  fail: {
    label: "fail",
    cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
    cell: "bg-red-500 dark:bg-red-600",
  },
  warn: {
    label: "warn",
    cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
    cell: "bg-amber-400 dark:bg-amber-500",
  },
  ok: {
    label: "ok",
    cls: "bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
    cell: "bg-emerald-500/70 dark:bg-emerald-600/70",
  },
};

/** fail → warn → ok (устойчиво: внутри статуса — порядок бэкенда). */
export function sortChecks(checks: WbCheck[]): WbCheck[] {
  return checks
    .map((c, i) => ({ c, i }))
    .sort((a, b) => CHECK_STATUS_ORDER[a.c.status] - CHECK_STATUS_ORDER[b.c.status] || a.i - b.i)
    .map((x) => x.c);
}

export const OBJECT_KIND_LABELS: Record<string, string> = {
  supply: "поставка",
  nm_id: "nm",
  // только у frozen_changed: замороженный остаток склада изменился (value — сейчас, expected — прошлый снимок)
  warehouse: "склад",
  source: "источник",
};

export interface HistoryStrip {
  dates: string[];
  rows: {
    key: string;
    legal: string;
    code: string;
    label: string | null;
    byDate: Map<string, WbCheckHistoryRow>;
  }[];
}

/** Полоса истории: строка — (кабинет, проверка), столбец — день периода истории. Порядок строк —
 *  как в таблице последних значений (`order` — ключи `кабинет|код`), остальные — после, по коду. */
export function historyStrip(rows: WbCheckHistoryRow[], labels: Record<string, string | null>, order: string[]): HistoryStrip {
  const dates = [...new Set(rows.map((r) => r.date))].sort();
  const map = new Map<string, HistoryStrip["rows"][number]>();
  for (const r of rows) {
    const key = `${r.legal_name}|${r.check_code}`;
    let row = map.get(key);
    if (!row) {
      row = { key, legal: r.legal_name, code: r.check_code, label: labels[r.check_code] ?? null, byDate: new Map() };
      map.set(key, row);
    }
    row.byDate.set(r.date, r);
  }
  const pos = (k: string): number => {
    const i = order.indexOf(k);
    return i < 0 ? order.length : i;
  };
  const list = [...map.values()].sort((a, b) => pos(a.key) - pos(b.key) || a.key.localeCompare(b.key));
  return { dates, rows: list };
}
